import * as Sentry from '@sentry/react';
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

Sentry.init({
  dsn: 'https://aa328a286639ca4e41f6933706e24f8c@o4511390038491136.ingest.de.sentry.io/4511390054219856',
  sendDefaultPii: true,
});

// After a deploy, a visitor's cached HTML can still reference chunk hashes that
// no longer exist on the server. Reload once to pick up the new HTML/manifest
// instead of surfacing a dead lazy-loaded route.
window.addEventListener('vite:preloadError', () => {
  const key = 'chunk-reload-attempted';
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1');
    window.location.reload();
  }
});

import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute   from './admin/ProtectedRoute';
import BugReportButton  from './components/BugReportButton';
import PixelTracker     from './components/PixelTracker';
import CookieBanner     from './components/CookieBanner';

// Public pages — eagerly loaded (all visitors need these)
import DrunkenLecturesLanding from './pages/drunken-lectures-landing.jsx';
import AboutUs                from './pages/about-us.jsx';
import ContactUs              from './pages/contact-us.jsx';
import PreviousLectures       from './pages/previous-lectures.jsx';
import PostDetail             from './pages/post-detail.jsx';
import News                   from './pages/news.jsx';
import Gallery                from './pages/gallery.jsx';
import Partnerships           from './pages/partnerships.jsx';
import Merch                  from './pages/merch.jsx';
import NotFound               from './pages/not-found.jsx';

// Admin pages — lazy loaded so public visitors never download this code
const AdminLogin     = lazy(() => import('./admin/AdminLogin.jsx'));
const AdminDashboard = lazy(() => import('./admin/AdminDashboard.jsx'));
const NewPostWizard  = lazy(() => import('./admin/NewPostWizard.jsx'));
const AcceptInvite   = lazy(() => import('./admin/AcceptInvite.jsx'));
const MfaChallenge   = lazy(() => import('./admin/MfaChallenge.jsx'));
const MfaSetup       = lazy(() => import('./admin/MfaSetup.jsx'));

function ErrorFallback({ resetError }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#FFFDF7', fontFamily: "'Inter', sans-serif", padding: '2rem',
    }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <img src="/Assets/drunken-lectures-logo.png" alt="Drunken Lectures" style={{ height: 48, marginBottom: '1.5rem' }} onError={e => { e.target.style.display = 'none'; }} />
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1A1A1A', margin: '0 0 0.5rem' }}>Something went wrong</h2>
        <p style={{ color: '#6A6A6A', fontSize: '0.9rem', margin: '0 0 1.5rem' }}>We've been notified and are looking into it.</p>
        <button
          onClick={resetError}
          style={{ padding: '10px 24px', background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function AdminFallback() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#FFFDF7', fontFamily: "'Inter', sans-serif",
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: '40px', height: '40px', border: '3px solid #F0EDE8',
          borderTopColor: '#FF6B35', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem',
        }} />
        <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
        <p style={{ color: '#6A6A6A', fontSize: '0.9rem', margin: 0 }}>Loading…</p>
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
      <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
          {/* ── Public ── */}
          <Route path="/"                element={<DrunkenLecturesLanding />} />
          <Route path="/about"           element={<AboutUs />} />
          <Route path="/contact"         element={<ContactUs />} />
          <Route path="/lectures"        element={<PreviousLectures />} />
          <Route path="/lectures/:slug"  element={<PostDetail />} />
          <Route path="/news"            element={<News />} />
          <Route path="/gallery"         element={<Gallery />} />
          <Route path="/partnerships"    element={<Partnerships />} />
          <Route path="/merch"           element={<Merch />} />

          {/* ── Admin — lazy loaded ── */}
          <Route path="/admin/login"         element={<Suspense fallback={<AdminFallback />}><AdminLogin /></Suspense>} />
          <Route path="/admin/accept-invite" element={<Suspense fallback={<AdminFallback />}><AcceptInvite /></Suspense>} />
          <Route path="/admin/mfa-challenge" element={<Suspense fallback={<AdminFallback />}><MfaChallenge /></Suspense>} />
          <Route path="/admin/mfa-setup"     element={<Suspense fallback={<AdminFallback />}><MfaSetup /></Suspense>} />
          <Route path="/admin"               element={<ProtectedRoute><Suspense fallback={<AdminFallback />}><AdminDashboard /></Suspense></ProtectedRoute>} />
          <Route path="/admin/new-post"      element={<ProtectedRoute><Suspense fallback={<AdminFallback />}><NewPostWizard /></Suspense></ProtectedRoute>} />
          <Route path="/admin/edit/:id"      element={<ProtectedRoute><Suspense fallback={<AdminFallback />}><NewPostWizard editMode /></Suspense></ProtectedRoute>} />

          {/* Redirect old WordPress-style path */}
          <Route path="/previous-lectures" element={<Navigate to="/lectures" replace />} />

          {/* Retired festival page (Mashilingi na Manoti, Oct 2026) */}
          <Route path="/festivals" element={<Navigate to="/" replace />} />

          {/* Catch-all 404 */}
          <Route path="*" element={<NotFound />} />
          </Routes>
          <BugReportButton />
          <PixelTracker />
          <CookieBanner />
        </BrowserRouter>
        <Analytics />
      </AuthProvider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
